import { inject, Injectable, signal, Type, ViewContainerRef } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';

import { EnvironmentService } from '@offconon/shared/utils/services/environment';
import { UrlService } from '@offconon/shared/utils/services/url';
export type ColumnName = 'leftBottom' | 'rightBottom' | 'leftTop' | 'rightTop' | 'contentHeader';

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private title = inject(Title);
  private meta = inject(Meta);

  private urlService = inject(UrlService);
  private environmentService = inject(EnvironmentService);
  private lastScrollTime = 0;
  private scrollThrottleMs = 250;

  private columnRefs = {
    leftBottom: signal<ViewContainerRef | null>(null),

    rightBottom: signal<ViewContainerRef | null>(null),

    leftTop: signal<ViewContainerRef | null>(null),

    rightTop: signal<ViewContainerRef | null>(null),

    contentHeader: signal<ViewContainerRef | null>(null),
  };

  columnVisibility = {
    leftBottom: signal(true),

    rightBottom: signal(true),

    leftTop: signal(true),

    rightTop: signal(true),

    contentHeader: signal(true),
  };
  componentInstances: { [key: string]: any } = {};

  contentCardNativeElement: HTMLElement;

  loadMore = new BehaviorSubject<{ page: number; rows: number } | null>(null);

  infiniteScrollState = signal<{
    pageSize: number;
    currentPage: number;
    totalItems: number;
  }>({
    pageSize: 10,
    currentPage: 0,
    totalItems: 0,
  });
  infiniteScrollLoading = signal(false);

  setTitleAndMeta(title: string, ogUrl = '', ogDescription = '', ogImage = '') {
    if (ogImage.startsWith('/')) {
      ogImage = ogImage.slice(1);
    }
    if (ogUrl.startsWith('/')) {
      ogUrl = ogUrl.slice(1);
    }

    this.title.setTitle(title);
    this.meta.removeTag('name="twitter:card"');
    this.meta.removeTag('name="og:url"');
    this.meta.removeTag('name="og:title"');
    this.meta.removeTag('name="og:description"');
    this.meta.removeTag('name="og:image"');
    this.meta.removeTag('name="description"');
    this.meta.removeTag('name="og:type"');
    this.meta.addTags([
      { name: 'twitter:card', content: 'summary' },
      {
        name: 'og:url',
        content:
          this.environmentService.environment.coreUrl +
          '/' +
          this.urlService.generateShareUrl(ogUrl),
      },
      { name: 'og:title', content: title },
      { name: 'og:description', content: ogDescription },
      {
        name: 'og:image',
        content: ogImage.includes('http')
          ? ogImage
          : this.environmentService.environment.coreUrl + '/' + ogImage,
      },
      { name: 'description', content: ogDescription },
      { name: 'og:type', content: 'website' },
    ]);
  }

  loadComponent<T>(component: Type<T>, name: ColumnName, inputs?: Record<string, any>) {
    const columnRef = this.columnRefs[name]();
    if (!columnRef) {
      this.columnVisibility[name].set(false);

      return;
    }

    const stored = this.componentInstances[name];

    const isSameComponent = stored?.componentType === component;
    if (stored && isSameComponent && !stored.instance.hostView?.destroyed) {
      const viewRef = stored.instance.hostView;
      const oldIdx = columnRef.indexOf(viewRef);
      if (oldIdx !== -1) {
        columnRef.detach(oldIdx);
      }

      columnRef.clear();

      columnRef.insert(stored.instance.hostView, 0);

      if (inputs) {
        Object.entries(inputs).forEach(([key, value]) => {
          stored.instance.setInput(key, value);
        });
        stored.instance.changeDetectorRef.detectChanges();
      }
    } else {
      if (stored) {
        stored.instance.destroy();
      }

      columnRef.clear();
      const newComponent = columnRef.createComponent(component);

      if (inputs) {
        Object.entries(inputs).forEach(([key, value]) => {
          newComponent.setInput(key, value);
        });
      }

      this.componentInstances[name] = {
        instance: newComponent,
        componentType: component,
      };
      newComponent.changeDetectorRef.detectChanges();
    }

    this.columnVisibility[name].set(true);
  }

  resetAllContainers() {
    (['leftBottom', 'rightBottom', 'leftTop', 'rightTop', 'contentHeader'] as ColumnName[]).forEach(
      (name: ColumnName) => {
        this.resetContainer(name);
      },
    );
  }
  resetContainer(name: ColumnName) {
    const columnRef = this.columnRefs[name]();
    if (!columnRef) {
      this.columnVisibility[name].set(false);

      return;
    }

    const stored = this.componentInstances[name];
    if (stored) {
      stored.instance.destroy();
      delete this.componentInstances[name];
    }

    columnRef.clear();
    this.columnVisibility[name].set(false);
  }

  checkContainer(name: ColumnName) {
    const componentInstance = this.componentInstances[name];

    if (!componentInstance) {
      this.columnVisibility[name].set(false);

      return;
    }

    this.columnVisibility[name].set(true);
  }

  checkAllContainers() {
    (['leftBottom', 'rightBottom', 'leftTop', 'rightTop', 'contentHeader'] as ColumnName[]).forEach(
      (name: ColumnName) => {
        this.checkContainer(name);
      },
    );
  }
  setColumnRef(ref: ViewContainerRef, name: ColumnName) {
    this.columnRefs[name].set(ref);
  }
  setContentCardNativeElement(nativeElement: any) {
    this.contentCardNativeElement = nativeElement;
  }

  onMainContentScroll(event: Event) {
    const now = Date.now();
    if (now - this.lastScrollTime < this.scrollThrottleMs) {
      return;
    }
    this.lastScrollTime = now;
    const element = event.currentTarget as HTMLElement;
    const scrollPosition = element.scrollTop;
    const viewportHeight = element.clientHeight;
    const totalHeight = element.scrollHeight;
    const distanceFromBottom = totalHeight - (scrollPosition + viewportHeight);

    if (this.shouldLoadMore(distanceFromBottom)) {
      const nextPage = this.infiniteScrollState().currentPage + 1;
      this.loadMore.next({
        page: nextPage - 1,
        rows: this.infiniteScrollState().pageSize,
      });
    }
  }
  private shouldLoadMore(distanceFromBottom: number): boolean {
    if (this.infiniteScrollLoading()) return false;
    const loadedItems =
      this.infiniteScrollState().currentPage * this.infiniteScrollState().pageSize;
    const hasMoreItems = loadedItems < this.infiniteScrollState().totalItems;
    return hasMoreItems && distanceFromBottom <= 300;
  }

  resetInfiniteScrollState() {
    this.loadMore.next(null);
    this.infiniteScrollState.set({
      pageSize: 10,
      currentPage: 0,
      totalItems: 0,
    });
    this.infiniteScrollLoading.set(false);
  }
}
